import * as React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, AuthService } from "../../../../services/openapi";

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = React.useState("");
  const [passwordRepeat, setPasswordRepeat] = React.useState("");
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(
    token.length === 0 ? "Der Einladungslink ist unvollständig." : undefined
  );

  const passwordMismatch = passwordRepeat.length > 0 && password !== passwordRepeat;
  const canSubmit =
    token.length > 0 &&
    password.length > 0 &&
    passwordRepeat.length > 0 &&
    !passwordMismatch &&
    termsAccepted &&
    !saving;

  const getErrorMessage = (err: unknown) => {
    if (err instanceof ApiError && err.body?.detail) {
      return String(err.body.detail);
    }
    return "Einladung konnte nicht angenommen werden.";
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSaving(true);
    setError(undefined);
    AuthService.acceptInviteAuthRegistrationAcceptInvitePost({
      token,
      password,
      termsAccepted,
    })
      .then(() => {
        setSuccess(true);
      })
      .catch((err) => {
        setError(getErrorMessage(err));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "white",
        p: 2,
      }}
    >
      <Avatar
        alt="Logo"
        src="/logo.svg"
        variant="square"
        sx={{
          position: "absolute",
          top: "20px",
          left: "20px",
          height: "80px",
          width: "80px",
        }}
      />
      <Card sx={{ width: "100%", maxWidth: 440 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
            Einladung annehmen
          </Typography>

          {success ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Alert severity="success">Ihre Einladung wurde angenommen.</Alert>
              <Button variant="contained" onClick={() => navigate("/auth")}>
                Zum Login
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Passwort"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={token.length === 0}
              />
              <TextField
                label="Passwort wiederholen"
                type="password"
                value={passwordRepeat}
                onChange={(event) => setPasswordRepeat(event.target.value)}
                error={passwordMismatch}
                helperText={passwordMismatch ? "Die Passwörter stimmen nicht überein." : ""}
                disabled={token.length === 0}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(event) => setTermsAccepted(event.target.checked)}
                    disabled={token.length === 0}
                  />
                }
                label={
                  <Typography variant="body2">
                    Ich stimme den{" "}
                    <Link href="/terms-of-service">Nutzungsbedingungen</Link>
                    {" "}und{" "}
                    <Link href="/privacy-policy">Datenschutzrichtlinien</Link>
                    {" "}zu
                  </Typography>
                }
              />
              <Button type="submit" variant="contained" disabled={!canSubmit}>
                {saving ? <CircularProgress size={20} /> : "Einladung annehmen"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
